import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AI_COUNTS_ENABLED } from "@/features/takeoff-pilot/ai-count-overlay";
import { AiTakeoffSetup, isAiSetupPending } from "@/features/takeoff-pilot/ai-takeoff-setup";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useRouteParam } from "@/lib/use-route-param";

export const Route = createFileRoute("/takeoffPilot/$jobId_/setup")({
  component: AiTakeoffSetupPage,
});

export function AiTakeoffSetupPage() {
  const jobId = useRouteParam("jobId") as Id<"takeoffPilotJobs"> | undefined;
  const navigate = useAppNavigate();
  const { data: details, isPending } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getWorkspaceDetails, jobId ? { jobId } : "skip")
  );
  const { data: setup } = useQuery(convexQuery(api.takeoffPilot.aiWorkflow.get,
    AI_COUNTS_ENABLED && details?.job.aiTakeoffRequested && jobId ? { jobId } : "skip"));
  const settled = details === null || (details !== undefined && (!AI_COUNTS_ENABLED || !details.job.aiTakeoffRequested || setup !== undefined));
  const pending = isAiSetupPending(details, setup);

  // Jobs that no longer need setup belong in the drawing viewer.
  useEffect(() => {
    if (jobId && details && settled && !pending) {
      navigate({ to: "/takeoffPilot/$jobId", params: { jobId }, title: details.job.name, replace: true });
    }
  }, [jobId, details, settled, pending, navigate]);

  if (details === null) {
    return (
      <div className="space-y-4">
        <PageHeader title="Takeoff Job Not Found" trail={[{ label: "TakeoffPilot", href: "/takeoffPilot" }]} />
        <Alert variant="destructive"><AlertDescription>This job may have been deleted or you may not have access.</AlertDescription></Alert>
      </div>
    );
  }
  if (isPending || !details || !setup || !pending) return <PageHeaderSkeleton withTrail />;
  return <AiTakeoffSetup key={`${details.job._id}:${setup.started}`} details={details} initial={setup} />;
}
